// Canonical event names + typed track() helper. Keeping event names as
// constants in one place makes it easy to audit what's instrumented and
// prevents typo drift (e.g. `signed_in` vs `sign_in_success`).
//
// GA4 conventions: snake_case, action-oriented, under 40 chars.
import { gtag, type GtagParams } from './gtag'
import { getPostHog } from './posthog'

export const AnalyticsEvents = {
  SC_CALLBACK_CANCELED: 'sc_callback_canceled',
  SC_REPORT_FILTERED: 'sc_report_filtered',
  SC_OUTBOUND_STARTED: 'sc_outbound_started',
  SC_PHONE_LAUNCHED: 'sc_phone_launched',
  SC_SETTINGS_SAVED: 'sc_settings_saved',
  SC_IVR_DRAFT_SAVED: 'sc_ivr_draft_saved',
  SC_IVR_PUBLISHED: 'sc_ivr_published',
  SC_PROMPT_SAVED: 'sc_prompt_saved',
  SC_PROMPT_UPLOADED: 'sc_prompt_uploaded',
  // Service Center (agent desktop in the internal portal)
  SC_CALL_WRAPPED: 'sc_call_wrapped',
  SC_AVAILABILITY_CHANGED: 'sc_availability_changed',
  SC_CALLER_CONTEXT_CONFIRMED: 'sc_caller_context_confirmed',
  SC_CALL_ESCALATED: 'sc_call_escalated',

  // Auth
  AUTH_SIGNED_IN: 'auth_signed_in',
  AUTH_SIGNED_OUT: 'auth_signed_out',
  AUTH_FAILED: 'auth_failed',
  IMPERSONATION_STARTED: 'impersonation_started',
  IMPERSONATION_ENDED: 'impersonation_ended',

  // Organization
  ORG_SWITCHED: 'org_switched',
  // A super admin drilled into an organization from the Admin landing/picker
  // (URL drill-in — does not change the viewer's active org).
  ADMIN_ORG_DRILL_IN: 'admin_org_drill_in',
  // A manager invited one or more members to their org from the in-app dialog.
  INVITATIONS_SENT: 'invitations_sent',
  // A new organization was stood up via the onboarding wizard (admin or Client
  // Success). `surface` distinguishes which app drove it.
  ORG_ONBOARDED: 'org_onboarded',
  // Client Success opened a customer user from the cross-org Customer Users
  // list into that org's Members page, where the edits happen.
  CUSTOMER_USER_OPENED: 'customer_user_opened',
  // A property search on the org drill-in grid returned (admin or Client
  // Success): "which org holds S0331?". Counts only, never the query.
  ORG_PROPERTY_SEARCHED: 'org_property_searched',
  // A technician organization (third-party field-tech company) was provisioned
  // via the dedicated FT- linkage flow (distinct from the customer wizard).
  TECHNICIAN_ORG_PROVISIONED: 'technician_org_provisioned',
  TECHNICIAN_COMPANY_INVITED: 'technician_company_invited',
  TECHNICIAN_COMPANY_INVITE_RESENT: 'technician_company_invite_resent',
  // Field Ops sent a vetted company the link that starts technician
  // onboarding (account, paperwork, MSA) — before it exists in BC.
  ONBOARDING_LINK_SENT: 'onboarding_link_sent',
  // An applicant saved one onboarding step (company, w9, insurance, …);
  // `complete` says whether it now counts as done.
  ONBOARDING_STEP_SAVED: 'onboarding_step_saved',
  // An applicant signed one onboarding document (`kind`: msa | w9) in the
  // embedded DocuSeal form.
  ONBOARDING_DOCUMENT_SIGNED: 'onboarding_document_signed',
  // An applicant step became complete (`step_key`, `journey_type`, `is_demo`)
  // — the agreement step included, once both documents are signed.
  ONBOARDING_STEP_COMPLETED: 'onboarding_step_completed',
  // The applicant's last required step completed and the journey went to
  // WorldVue for review (`journey_type`, `is_demo`).
  ONBOARDING_SUBMITTED: 'onboarding_submitted',
  // WorldVue's review of an onboarding: Accounting recorded the BC vendor,
  // a reviewer sent a step back, Field Ops approved (`is_demo`).
  ONBOARDING_ACCOUNTING_SIGNED_OFF: 'onboarding_accounting_signed_off',
  ONBOARDING_CHANGES_REQUESTED: 'onboarding_changes_requested',
  ONBOARDING_APPROVED: 'onboarding_approved',
  // Claim-your-hotel (brand compliance). Never the inn code or any email:
  // `is_demo` and the outcome kind are enough to see the funnel.
  CLAIM_STARTED: 'claim_started',
  CUSTOMER_SIGNUP_STARTED: 'customer_signup_started',
  // A website sign-up moving along: its contract, and sales playing
  // WorldVue's side of a demo from Sales › Sign-ups.
  CUSTOMER_CONTRACT_SIGNED: 'customer_contract_signed',
  SIGNUP_DEMO_ADVANCED: 'signup_demo_advanced',
  SIGNUP_DEMO_RESET: 'signup_demo_reset',
  CLAIM_REPORT_FILED: 'claim_report_filed',
  // WorldVue's side of brand compliance: a self-report passed or failed in
  // the review queue, and a month frozen or sent in Monthly reports.
  COMPLIANCE_REPORT_REVIEWED: 'compliance_report_reviewed',
  COMPLIANCE_MONTH_CLOSE_STEP: 'compliance_month_close_step',
  COMPLIANCE_SAMPLES_VIEWED: 'compliance_samples_viewed',
  // A technician added a work note to a work order task. Same name atlas-mobile
  // fires (FieldEvents.TASK_NOTE_ADDED), so web and app land in one series;
  // `surface` tells them apart.
  TECHNICIAN_TASK_NOTE_ADDED: 'field_task_note_added',
  TECHNICIAN_INVOICE_PDF_DOWNLOADED: 'technician_invoice_pdf_downloaded',
  // A Technician Manager changed who in their org is on a task (Atlas-native
  // assignment). Same name atlas-mobile fires; `surface` tells them apart.
  TECHNICIAN_TASK_ASSIGNED: 'field_task_assigned',
  // An on-call responder hit Pick up on a Technician Assist page (the sticky
  // toast from the app-wide pager). `waited_seconds` = how long the tech had
  // been waiting — the number the unclaimed alert will be tuned against.
  ASSIST_PAGE_PICKED_UP: 'assist_page_picked_up',
  // A technician opened an assist conversation on the web (the target of a
  // reply notification). `surface` = where it was opened.
  ASSIST_CONVERSATION_OPENED: 'assist_conversation_opened',
  // A supervisor (support:assist:manage) switched someone else on or off
  // call from the workbench roster panel. `available` = the new state.
  ASSIST_ROSTER_UPDATED: 'assist_roster_updated',
  // An admin assigned one or more properties to an org via the "Add
  // properties" dialog. `group_count` is how many of the org's groups the
  // properties were also fanned out to in the optional second step (0 = org
  // only).
  PROPERTIES_ASSIGNED: 'properties_assigned',
  // Properties selected from an uploaded spreadsheet inside an Add properties
  // dialog (saving them still fires that dialog's own event). `entity_type` is
  // the target, or 'onboarding'; `problem_count` rows were refused (unknown
  // number, not in the org, …) without stopping the rest.
  PROPERTIES_IMPORTED: 'properties_imported',
  // An admin assigned users to a role. `role_type` (system|shared|organization)
  // distinguishes the org-picker shared-role flow from the plain one.
  ROLE_USERS_ASSIGNED: 'role_users_assigned',
  ROLE_COPIED: 'role_copied',
  // An admin changed a role's linked Entra groups from the roles table.
  ROLE_ENTRA_GROUPS_UPDATED: 'role_entra_groups_updated',
  // Staff or a Portal Manager moved an org member one step along the portal
  // ladder (Read → Write → Manager) from a members table. `to_rank` is 1–3;
  // `surface` is the hosting app ('portal' for the Portal Members page).
  PORTAL_ROLE_PROMOTED: 'portal_role_promoted',
  PORTAL_ROLE_DEMOTED: 'portal_role_demoted',
  // A member was removed from an org. `surface` is the hosting page.
  ORG_MEMBER_REMOVED: 'org_member_removed',
  // A member was put in, moved between or taken out of an org group.
  // `action` is added | moved | removed; `surface` is the hosting page.
  MEMBER_GROUP_CHANGED: 'member_group_changed',

  // Initiatives (org-level entity groupings managed in Internal utilities)
  INITIATIVE_CREATED: 'initiative_created',
  INITIATIVE_UPDATED: 'initiative_updated',
  INITIATIVE_DELETED: 'initiative_deleted',
  INITIATIVE_CSV_IMPORTED: 'initiative_csv_imported',

  // Cases / work orders / proposals
  CASE_CREATED: 'case_created',
  INSTALLS_REVIEWED: 'installs_reviewed',
  // Spaces & racks (internal property pages)
  RACK_CREATED: 'rack_created',
  RACK_UPDATED: 'rack_updated',
  SPACE_CREATED: 'space_created',
  EQUIPMENT_PLACED: 'equipment_placed',
  RACK_DEVICE_MOVED: 'rack_device_moved',
  RACK_ITEM_RENAMED: 'rack_item_renamed',
  PROPERTY_PACKAGE_EXPORTED: 'property_package_exported',
  PLAN_PACKAGE_ISSUED: 'plan_package_issued',
  PLAN_PACKAGE_CERTIFIED: 'plan_package_certified',
  PLAN_PACKAGE_DOWNLOADED: 'plan_package_downloaded',
  SPACES_RECONCILE_OPENED: 'spaces_reconcile_opened',
  RACK_PHOTO_VIEWER_OPENED: 'rack_photo_viewer_opened',
  RACK_PHOTO_BOX_SAVED: 'rack_photo_box_saved',
  RACK_PHOTO_BOX_EDITED: 'rack_photo_box_edited',
  RACK_PHOTO_BOX_DELETED: 'rack_photo_box_deleted',
  RACK_PHOTO_CALIBRATED: 'rack_photo_calibrated',
  RACK_PHOTO_SUGGESTION_ACCEPTED: 'rack_photo_suggestion_accepted',
  RACK_PHOTO_SUGGESTION_DISMISSED: 'rack_photo_suggestion_dismissed',
  RACK_PHOTO_SUGGESTIONS_RERUN: 'rack_photo_suggestions_rerun',
  RACK_PHOTO_STRAIGHTEN_TOGGLED: 'rack_photo_straighten_toggled',
  RACK_PHOTO_CORNERS_ADJUSTED: 'rack_photo_corners_adjusted',
  // Rack photo retake review: opened, and each item resolved ({ kind, choice })
  RACK_RETAKE_REVIEW_OPENED: 'rack_retake_review_opened',
  RACK_RETAKE_ITEM_RESOLVED: 'rack_retake_item_resolved',
  RACK_BOX_FOCUSED: 'rack_box_focused',
  RACK_BOX_FOCUS_SAVED: 'rack_box_focus_saved',
  RACK_WALK_STARTED: 'rack_walk_started',
  RACK_WALK_FINISHED: 'rack_walk_finished',
  RACK_QR_GENERATED: 'rack_qr_generated',
  RACK_LABELS_PRINTED: 'rack_labels_printed',
  UNIDENTIFIED_DEVICE_RESOLVED: 'unidentified_device_resolved',
  PLAN_UPLOADED: 'plan_uploaded',
  PLAN_SCALE_SET: 'plan_scale_set',
  PLAN_MARKER_PLACED: 'plan_marker_placed',
  PLAN_SPACE_RESHAPED: 'plan_space_reshaped',
  PLAN_OBJECT_ADJUSTED: 'plan_object_adjusted',
  // Plans › catalog: a BC model placed on a plan, and a plan linked to a BC project
  PLAN_CATALOG_ITEM_PLACED: 'plan_catalog_item_placed', // params: { category, qty, icon }
  PLAN_CATALOG_ITEM_REMOVED: 'plan_catalog_item_removed',
  PLAN_CATALOG_JOB_LINKED: 'plan_catalog_job_linked', // params: { linked }
  // Outdoor site plan (satellite map): a marker pinned, a run drawn, a photo added.
  PLAN_SITE_MARKER_PLACED: 'plan_site_marker_placed',
  PLAN_SITE_RUN_DRAWN: 'plan_site_run_drawn',
  PLAN_SITE_PHOTO_ADDED: 'plan_site_photo_added',
  // Site plan polish: a saved run reshaped, the map centered from the address.
  PLAN_SITE_RUN_RESHAPED: 'plan_site_run_reshaped', // params: { point_count }
  PLAN_SITE_CENTER_GEOCODED: 'plan_site_center_geocoded', // params: { ok }
  PLAN_SITE_EXPORT_VIEW_SET: 'plan_site_export_view_set', // params: { locked, rotated }
  PLAN_LINES_DARKENED: 'plan_lines_darkened',
  PLAN_LINES_RESTORED: 'plan_lines_restored',
  CAPTURE_RUN_REVIEWED: 'capture_run_reviewed',
  // Capture templates admin (Field Operations): ops define what technicians
  // collect per type of work. Params carry counts and kinds, never free text.
  CAPTURE_TEMPLATE_SAVED: 'capture_template_saved',
  CAPTURE_TEMPLATE_TOGGLED: 'capture_template_toggled',
  CAPTURE_RULE_SAVED: 'capture_rule_saved',
  CAPTURE_RULE_DELETED: 'capture_rule_deleted',
  CAPTURE_STARTERS_INSTALLED: 'capture_starters_installed',
  // Property site data and visit health (Field Operations).
  SITE_DATA_VIEWED: 'site_data_viewed',
  FIELD_DATA_VIEWED: 'field_data_viewed',
  FIELD_DATA_EXPORTED: 'field_data_exported',
  VISIT_SNAPSHOT_TAKEN: 'visit_snapshot_taken',
  VISIT_QA_VIEWED: 'visit_qa_viewed',
  // Work order task page: a capture photo opened full size.
  TASK_PHOTO_VIEWED: 'task_photo_viewed',
  // Triage: an email-created case with no customer gets assigned to one
  CASE_ASSIGNED_TO_CUSTOMER: 'case_assigned_to_customer',
  INCIDENT_CREATED: 'incident_created',
  // Fires on reuse too: the API answers a reused contact like a new one.
  CONTACT_CREATED: 'contact_created', // params: { has_email, has_phone }
  // Contact merge review queue (Internal -> Utilities). One event per
  // decision, however many pairs it covered, so `count` says how big it was.
  CONTACT_REVIEW_DECIDED: 'contact_review_decided', // params: { action, scope, count }
  CONTACT_REVIEW_UNDONE: 'contact_review_undone', // params: { rows }
  // A customer's Contacts tab (Atlas#1046). Searched fires once per
  // committed search, with its length only, never the text.
  CONTACT_DIRECTORY_VIEWED: 'contact_directory_viewed', // params: { contacts, contacts_shown, surface }
  CONTACT_DIRECTORY_SEARCHED: 'contact_directory_searched', // params: { query_length, surface }
  // A person's page (Atlas#1049), and a correction saved from it or from a
  // case contact row. Field names and counts only, never the values.
  CONTACT_PERSON_VIEWED: 'contact_person_viewed', // params: { sites, sources, linked_records }
  CONTACT_CORRECTED: 'contact_corrected', // params: { fields, field_count, reverted, surface }
  AI_AGENT_DETAIL_OPENED: 'ai_agent_detail_opened', // params: { agent, health }
  // Work orders originate in ServiceNow; the high-value Atlas-side action is
  // a dispatcher pushing a WO to a tech (new task or re-dispatch of existing).
  WORK_ORDER_DISPATCHED: 'work_order_dispatched',
  // Agent requests a WO from a case (created in Awaiting Approval).
  WORK_ORDER_REQUESTED: 'work_order_requested',
  // A reviewer's decision on a request — the Lead's or Dispatch's (`stage`).
  // Dispatch's approval is the point a work order comes into existence.
  WORK_ORDER_REQUEST_REVIEWED: 'work_order_request_reviewed',
  // An agent revises their own draft or returned request — how often a Lead's
  // "send back" actually comes back, and how fast — or a Support Lead revises
  // another agent's, or one under review (`actor_stage` tells them apart).
  WORK_ORDER_REQUEST_REVISED: 'work_order_request_revised',
  // An agent abandons their own draft or returned request, or a Support Lead
  // clears another agent's draft (`actor_stage` tells them apart).
  WORK_ORDER_REQUEST_DISCARDED: 'work_order_request_discarded',
  // An agent calls off work they raised — the other half of the request.
  WORK_ORDER_CANCELLED: 'work_order_cancelled',
  // A Support Lead corrects an open work order. `state` and `has_tasks` show
  // whether the edits land before dispatch or after the tasks took the scope.
  WORK_ORDER_EDITED: 'work_order_edited',
  // Agent submits a ServiceNow equipment catalog request off a case.
  EQUIPMENT_REQUESTED: 'equipment_requested',
  // Deployment project milestones — populate builds the scheduled ladder;
  // completing stamps the actual date (PM "Done" button).
  PROJECT_MILESTONES_POPULATED: 'project_milestones_populated',
  PROJECT_MILESTONE_COMPLETED: 'project_milestone_completed',
  // Deployment project install dates — a PM wrote the forecast/actual install
  // dates through to the Business Central job card. Params carry which fields
  // changed, never the values.
  PROJECT_INSTALL_DATES_UPDATED: 'project_install_dates_updated',
  // Deployment tools — a config generator produced output (tool name in params).
  DEPLOYMENT_TOOL_CONFIG_GENERATED: 'deployment_tool_config_generated',
  // Deployment tools launcher — a search settled (query length + result
  // count, never the query), and a tool tile was opened (its slug).
  DEPLOYMENT_TOOL_SEARCHED: 'deployment_tool_searched',
  DEPLOYMENT_TOOL_OPENED: 'deployment_tool_opened',
  // Gateway — an operator ended a guest's hotspot session on the property's
  // MikroTik (Internal › Gateway › Sessions).
  GATEWAY_SESSION_DISCONNECTED: 'gateway_session_disconnected',
  // Gateway — an operator let a device past the captive portal, blocked one,
  // or removed a binding on the property's MikroTik (Internal › Gateway ›
  // Devices). `path` param on the bypass: which writer applied it.
  GATEWAY_DEVICE_BYPASSED: 'gateway_device_bypassed',
  GATEWAY_DEVICE_BLOCKED: 'gateway_device_blocked',
  GATEWAY_BINDING_REMOVED: 'gateway_binding_removed',
  // Gateway — an operator changed the router's RADIUS client table
  // (Internal › Gateway › RADIUS). `action` param: enabled/disabled/added/removed.
  GATEWAY_RADIUS_SERVER_CHANGED: 'gateway_radius_server_changed',
  // Gateway — an operator switched the guest authentication method
  // (`method` param) or saved the hotspot parameters.
  GATEWAY_AUTH_METHOD_CHANGED: 'gateway_auth_method_changed',
  GATEWAY_HOTSPOT_PARAMS_UPDATED: 'gateway_hotspot_params_updated',
  // An operator changed the hotspot's walled garden on a property's MikroTik
  // (Internal › Gateway › Access Policy). `action` param: added/enabled/disabled/removed.
  GATEWAY_WALLED_GARDEN_CHANGED: 'gateway_walled_garden_changed',
  // An operator changed the pay access plans on a property's MikroTik
  // (Internal › Gateway › Access Policy). `action` param: added/updated/removed/moved/currency.
  GATEWAY_PAY_PLAN_CHANGED: 'gateway_pay_plan_changed',
  // An operator changed connection limiting or the P2P posture on a property's
  // MikroTik (Internal › Gateway › Access Policy). Params: kind, enabled | mode.
  GATEWAY_LIMITS_CHANGED: 'gateway_limits_changed',
  // Brand Standard section: a vendor pre-stage plan was built for a property.
  // params: { standard, step_count, ready, surface }
  NETWORK_PRESTAGE_PLAN_BUILT: 'network_prestage_plan_built',
  // Brand Standard section: the live network was read and scored.
  // params: { standard, pass_count, fail_count, unknown_count, surface }
  NETWORK_LIVE_CHECK_RUN: 'network_live_check_run',
  // Brand Standard section: a live read was kept as a stored backup.
  NETWORK_SNAPSHOT_SAVED: 'network_snapshot_saved',
  // Brand Standard section: a stored backup was moved to the recycle bin.
  NETWORK_SNAPSHOT_DELETED: 'network_snapshot_deleted',
  // Brand Standard section: the pre-stage was sent to the dashboard.
  // params: { standard, applied, failed, skipped, completed, surface }
  NETWORK_PRESTAGE_PUSHED: 'network_prestage_pushed',
  /** A restore plan was built from a stored Meraki snapshot. */
  NETWORK_RESTORE_PLAN_BUILT: 'network_restore_plan_built',
  // Brand Standard section: a plan was read back from the dashboard.
  // params: { standard, match, differs, unavailable, surface }
  NETWORK_PLAN_VERIFIED: 'network_plan_verified',
  // Brand Standard section: the standard's VLANs and DHCP pools were recorded
  // as planned inventory rows. params: { standard, vlans_added, scopes_added }
  NETWORK_PLAN_RECORDED: 'network_plan_recorded',
  // Internal › Networking › VLANs / DHCP Scopes (inventory.network_vlan,
  // inventory.dhcp_scope). params: { status, dhcp_mode | served_by }
  NETWORK_VLAN_CREATED: 'network_vlan_created',
  NETWORK_VLAN_UPDATED: 'network_vlan_updated',
  DHCP_SCOPE_CREATED: 'dhcp_scope_created',
  DHCP_SCOPE_UPDATED: 'dhcp_scope_updated',
  // An operator created a VLAN on a property's MikroTik (Internal › Gateway ›
  // VLANs). Params: placement, plus whether it got an address, DHCP, a
  // captive portal and a cap — the shape of the segment, never its addresses.
  GATEWAY_VLAN_CREATED: 'gateway_vlan_created',
  // An operator removed a VLAN and everything the router held for it
  // (Internal › Gateway › VLANs). Param: tag.
  GATEWAY_VLAN_REMOVED: 'gateway_vlan_removed',
  // An operator moved an ethernet port between bridges, or off them, on a
  // property's MikroTik (Internal › Gateway › VLANs). Params: port, and
  // whether it landed on a bridge or was freed.
  GATEWAY_PORT_NETWORK_CREATED: 'gateway_port_network_created',
  GATEWAY_PORT_NETWORK_REMOVED: 'gateway_port_network_removed',
  GATEWAY_BRIDGE_PORT_MOVED: 'gateway_bridge_port_moved',
  // An operator renamed, re-clocked or rebooted a property's MikroTik
  // (Internal › Gateway › System). `action` param: identity/clock/reboot.
  GATEWAY_SYSTEM_CHANGED: 'gateway_system_changed',
  // Internal › Gateway › Tools: a diagnostic ran on the router.
  // params: { tool: <the Tool union in gateway-tools.tsx>, ok, reason? }
  GATEWAY_TOOL_RUN: 'gateway_tool_run',
  TASK_DISPATCHED: 'task_dispatched',
  // Technician Portal — the technician's side of a work-order task. Each is the
  // success branch of a SNOW state transition driven from the field app.
  // Params carry task/work-order numbers + surface only — never PII.
  FIELD_TASK_ACCEPTED: 'field_task_accepted',
  FIELD_TASK_ESTIMATE_UPDATED: 'field_task_estimate_updated',
  FIELD_TASK_REJECTED: 'field_task_rejected',
  FIELD_TASK_EN_ROUTE: 'field_task_en_route',
  FIELD_TASK_WORK_STARTED: 'field_task_work_started',
  FIELD_TASK_WORK_ENDED: 'field_task_work_ended',
  FIELD_TASK_ACTUALS_SAVED: 'field_task_actuals_saved',
  FIELD_TASK_ACTUALS_SUBMITTED: 'field_task_actuals_submitted',
  COST_APPROVAL_DECIDED: 'cost_approval_decided',
  CASE_INVOICE_RELEASED: 'case_invoice_released',
  // A reviewer opened the property's signed proposal (or its audit trail)
  // from an invoice. Params: document, proposal_kind — no names.
  INVOICE_PROPOSAL_OPENED: 'invoice_proposal_opened',
  SMARTBOX_SERVICES_MUTED: 'smartbox_services_muted',
  SMARTBOX_SERVICES_UNMUTED: 'smartbox_services_unmuted',
  // Non-payment cut of a property's guest internet at its MikroTik, and its
  // restore. Params: customer_no, outcome (the router's named result).
  GUEST_INTERNET_DISCONNECTED: 'guest_internet_disconnected',
  GUEST_INTERNET_RESTORED: 'guest_internet_restored',
  // Operator-initiated captive-portal bypass for one guest device. Fired on
  // every attempt, with the named outcome as a param — the interesting question
  // is how often a bypass is refused (property not onboarded, gateway down,
  // device already gone) versus applied.
  GUEST_BYPASS_APPLIED: 'guest_bypass_applied',
  FIELD_TASK_COMPLETED: 'field_task_completed',
  FIELD_TASK_FILE_UPLOADED: 'field_task_file_uploaded',
  // Technician costs v2 — a cost line recorded, corrected or removed.
  FIELD_COST_SAVED: 'field_cost_saved',
  FIELD_COST_DELETED: 'field_cost_deleted',
  FIELD_QUESTIONNAIRE_SUBMITTED: 'field_questionnaire_submitted',
  // Shipment tracking (PostHog `technician-shipments`). Same names atlas-mobile
  // fires (FieldEvents.SHIPMENT_*), so both clients land in one series; the
  // `platform` super-property tells them apart. Enums and counts only — a
  // tracking number, recipient or property is never a param.
  // The Equipment card on a work order rendered. params: { task_number, readiness, boxes }
  FIELD_SHIPMENT_CARD_VIEWED: 'field_shipment_card_viewed',
  // A package was opened. params: { surface: task-card|equipment-list, status, direction }
  FIELD_SHIPMENT_PACKAGE_OPENED: 'field_shipment_package_opened',
  // "Open in FedEx" was clicked. params: { status }
  FIELD_SHIPMENT_TRACKING_OPENED: 'field_shipment_tracking_opened',
  // The Equipment (Shipped to me) page rendered. params: { in_transit, delivered }
  FIELD_SHIPMENT_EQUIPMENT_VIEWED: 'field_shipment_equipment_viewed',
  // The Equipment "To my job sites" tab rendered. params: { sites, mine, at_risk }
  FIELD_SHIPMENT_SITES_VIEWED: 'field_shipment_sites_viewed',
  // Dispatching › Parts readiness. Web-only today; mobile's DispatchEvents has
  // no counterpart. params: viewed { visits, direct }; filtered { filter: view|group, value }
  DISPATCH_PARTS_READINESS_VIEWED: 'dispatch_parts_readiness_viewed',
  DISPATCH_PARTS_READINESS_FILTERED: 'dispatch_parts_readiness_filtered',
  // A customer opened the "Equipment for your technician visit" card on a
  // portal case. params: { state: delivered|in_transit|delayed, boxes }
  PORTAL_CASE_EQUIPMENT_VIEWED: 'portal_case_equipment_viewed',
  // Technician guided tour — a practice work order walked through the real
  // pages. `exited` carries the stage the technician left on, which is the
  // only way to find the step that isn't working.
  FIELD_TOUR_STARTED: 'field_tour_started',
  FIELD_TOUR_COMPLETED: 'field_tour_completed',
  FIELD_TOUR_EXITED: 'field_tour_exited',
  // Every guided tour run by the tours engine (features/tours). The FIELD_TOUR_*
  // names above keep firing for the technician tour alongside these, because
  // atlas-mobile sends them too. params: { tour_id, variant, flow, step,
  // step_index (1-based in its flow), trigger, replay, duration_s,
  // after_completion, reason (exited only: user|org-switch) }.
  // COMPLETED fires once per flow completed, so an encore adds a second.
  TOUR_STARTED: 'tour_started',
  TOUR_COMPLETED: 'tour_completed',
  TOUR_EXITED: 'tour_exited',
  PROPOSAL_CREATED: 'proposal_created',
  // CRM opportunity write-back (Dynamics writes from Atlas)
  OPPORTUNITY_CREATED: 'opportunity_created',
  OPPORTUNITY_UPDATED: 'opportunity_updated',
  OPPORTUNITY_CLOSED: 'opportunity_closed', // params: { outcome: won|lost|reopen }
  OPPORTUNITY_STAGE_CHANGED: 'opportunity_stage_changed',
  // A burn-in proposal (dispatch/equipment) was sent for e-signature via
  // DocuSeal. Context-agnostic — surface includes the context type.
  PROPOSAL_SENT_FOR_SIGNATURE: 'proposal_sent_for_signature',
  // A Design Intake magic link was sent to a property contact.
  DESIGN_INTAKE_SENT: 'design_intake_sent',
  // Staff issued a form a new public link (ending the old one) to share
  // themselves; links are stored hashed, so this replaced "copy link".
  DESIGN_INTAKE_LINK_REISSUED: 'design_intake_link_reissued',
  // Staff ended a form's public link without issuing another.
  DESIGN_INTAKE_LINK_REVOKED: 'design_intake_link_revoked',
  // Tier 0 text link: staff fired a test link from the internal test page.
  // params: { simulate: tier0|queue_hold, create_case }
  TEXT_LINK_TEST_SENT: 'text_link_test_sent',
  // A caller submitted the text-link form (public page).
  // params: { source, report_type, upload_count, case_created }
  TEXT_LINK_SUBMITTED: 'text_link_submitted',
  // Staff texted an Evidence Links request (upload files to a case).
  // params: { channel: sms, ask_count, to_contact }
  EVIDENCE_REQUEST_SENT: 'evidence_request_sent',
  // Staff accepted or dismissed who answered an Evidence Links request.
  // params: { decision: accept|dismiss, new_contact, change_count }
  EVIDENCE_CONTACT_REVIEWED: 'evidence_contact_reviewed',
  CANCELLATION_CREATED: 'cancellation_created', // params: { cancellation_type, reason_code, service_count, has_cancel_date }
  CHANGE_ORDER_CREATED: 'change_order_created', // params: { service_category, cost_party, has_baseline, baseline_source, timeline_delay, design_needed, surface }
  CHANGE_ORDER_TRANSITIONED: 'change_order_transitioned', // params: { action, from_status, surface }
  CHANGE_ORDER_PDF_OPENED: 'change_order_pdf_opened', // params: { status, draft, surface }
  CHANGE_ORDER_SENT_FOR_SIGNATURE: 'change_order_sent_for_signature', // params: { emailed, contact_edited, surface }
  // The six-step process (#1010): a step advance, or a close as saved.
  CANCELLATION_STEP_CHANGED: 'cancellation_step_changed', // params: { from, to }
  CANCELLATION_FORM_SENT: 'cancellation_form_sent', // params: { has_name, emailed }
  CANCELLATION_STATUS_CHANGED: 'cancellation_status_changed', // params: { to: saved|cancellation_complete }
  // Company review queue and fix-in-BC list decisions (companies:update).
  COMPANY_REVIEW_DECIDED: 'company_review_decided', // params: { decision: accept|reject|dismiss, kind, surface: queue }
  // Company edits (companies:update).
  COMPANY_DETAILS_UPDATED: 'company_details_updated', // params: { fields_changed }
  COMPANY_ROLE_CHANGED: 'company_role_changed', // params: { action: add|remove, role }
  COMPANY_PARENT_CHANGED: 'company_parent_changed', // params: { cleared }
  COMPANY_ORG_LINKED: 'company_org_linked', // params: { cleared }
  COMPANY_TEAM_ASSIGNED: 'company_team_assigned', // params: { team_role, unassigned }
  COMPANY_VIEWED: 'company_viewed', // params: { is_technician_company, property_count }
  DESIGN_TASK_ACTION_RUN: 'design_task_action_run', // params: { action, from, to, service, surface: board|detail }
  DESIGN_REQUEST_SUBMITTED: 'design_request_submitted', // params: { opportunity_count, task_count, services, intake_requested, attachment_count, surface }
  BOM_REQUESTED: 'bom_requested', // params: { job_no, installation_type_count, surface }
  DESIGN_PRIORITY_SETTINGS_SAVED: 'design_priority_settings_saved', // params: { surface }
  DESIGN_HIVE_IMPORTED: 'design_hive_imported', // params: { requests, tasks, already_imported }
  DESIGN_REQUEST_NOTE_ADDED: 'design_request_note_added', // params: { request_id, task_id, note_length }
  AP_DESIGNER_ANALYSIS_STARTED: 'ap_designer_analysis_started', // params: { floor_count, selected }
  AP_DESIGNER_RENDER_STARTED: 'ap_designer_render_started', // params: { floor_count }
  AP_DESIGNER_SENT_TO_TASK: 'ap_designer_sent_to_task', // params: { filed }
  AP_DESIGNER_FILL_TYPE: 'ap_designer_fill_type', // params: { kind: ap|data, moved }
  AP_DESIGNER_REFERENCE_PAGE: 'ap_designer_reference_page', // params: { action: make|floor|delete }
  AP_DESIGNER_SUITE: 'ap_designer_suite', // params: { action: merge|split, bays }
  AP_DESIGNER_LOGICAL_REVIEW: 'ap_designer_logical_review', // opened the Logical design review
  DESIGN_AP_PROPERTY_CREATED: 'design_ap_property_created', // params: { for_task, has_opportunity, has_intake, floor_plan_files, surface: list|task }

  // Files (legacy SMB-backed)
  FILE_UPLOADED: 'file_uploaded',

  // Customer files (new SeaweedFS-backed, context-aware)
  CUSTOMER_FILE_UPLOADED: 'customer_file_uploaded',
  CUSTOMER_FILE_DELETED: 'customer_file_deleted',
  CUSTOMER_FILE_RESTORED: 'customer_file_restored',
  CUSTOMER_FILE_RENAMED: 'customer_file_renamed',
  CUSTOMER_FILE_RETAGGED: 'customer_file_retagged',
  CUSTOMER_FILE_PERMANENTLY_DELETED: 'customer_file_permanently_deleted',

  // Customer identity strip. `surface` says which app's sub-header it came
  // from, since the strip renders for internal, dispatching and technician.
  CUSTOMER_ID_COPIED: 'customer_id_copied',

  // Search
  SEARCH_PERFORMED: 'search_performed',
  // Advanced Search (Customers tab) submitted. `services_sold_count` tracks
  // adoption of the Services Sold filter.
  CUSTOMER_ADVANCED_SEARCHED: 'customer_advanced_searched',
  // Channel Impact lookup: a takedown search was committed. `included_siblings`
  // is the one to watch — if users routinely decline the SD/HD prompt, the
  // default is wrong and siblings should be included automatically.
  CHANNEL_IMPACT_SEARCHED: 'channel_impact_searched',
  // Topology map: user located a device via the search box (explicit pick,
  // not a deep-link / programmatic focus).
  TOPOLOGY_DEVICE_SELECTED: 'topology_device_selected',
  // Installed equipment: staff manually provisioned a device against a property
  INSTALLED_EQUIPMENT_CREATED: 'installed_equipment_created',
  // …and edited one afterwards. Separate from created so we can tell
  // provisioning volume from correction churn — a high edit rate on freshly
  // added devices means the add flow is getting something wrong.
  INSTALLED_EQUIPMENT_UPDATED: 'installed_equipment_updated',
  // …and provisioned a batch of them from a spreadsheet. Carries the row counts
  // on both sides of validation: a persistently high skipped:imported ratio
  // means the template or its error messages are failing people, not that they
  // can't type.
  INSTALLED_EQUIPMENT_IMPORTED: 'installed_equipment_imported',

  // Voice agent
  VOICE_AGENT_STARTED: 'voice_agent_started',
  // A live call opened from the console's live pill; `surface` names the entry point
  MIA_LIVE_CALL_OPENED: 'mia_live_call_opened',
  // Mia paused or resumed from the Configuration tab; `has_note` says whether a reason was given
  MIA_PAUSED: 'mia_paused',
  MIA_RESUMED: 'mia_resumed',
  // Overview metrics switched between Today, Yesterday and Last month; `period` is the pick
  MIA_METRICS_PERIOD_CHANGED: 'mia_metrics_period_changed',
  // The Configuration save bar saved; `sections` is comma-joined section keys
  MIA_SETTINGS_SAVED: 'mia_settings_saved',
  // That save switched the active transport; `from` and `to` are transport keys
  MIA_TRANSPORT_CHANGED: 'mia_transport_changed',
  // Call pressed in a transfer queue's Test popover; `queue` is the queue key
  MIA_TRANSFER_QUEUE_CALLED: 'mia_transfer_queue_called',

  // Vue feedback — a user rated or commented on an assistant answer. Params
  // carry sentiment + whether a comment was included, never the comment text.
  VUE_FEEDBACK_SUBMITTED: 'vue_feedback_submitted',

  // Vue thread search — a user opened a thread from the search overlay
  VUE_SEARCH_RESULT_OPENED: 'vue_search_result_opened',

  // A knowledge page cited in a Vue answer was opened. `section` is the KB's
  // top-level section (as on knowledge_article_viewed), `in_place` whether it
  // opened beside the panel rather than in a new tab.
  VUE_KNOWLEDGE_LINK_OPENED: 'vue_knowledge_link_opened',

  // Vue welcome surface — a user picked a suggested prompt. Carries the category
  // and not the text prompt, so we can see which topics drive first messages.
  VUE_SUGGESTION_SELECTED: 'vue_suggestion_selected',

  // Vue Help & Tips surface. `opened` carries where it was opened from; the
  // topic/example events carry the topic id
  VUE_HELP_OPENED: 'vue_help_opened',
  VUE_HELP_TOPIC_VIEWED: 'vue_help_topic_viewed',
  VUE_HELP_EXAMPLE_INSERTED: 'vue_help_example_inserted',

  // Vue Console — agent triage sessions with real fleet tools, through the
  // `/api/vector` relay. `via` is `skill | message`; `mid_turn` marks a message
  // typed into a running turn (a steer) rather than one that started a turn.
  //
  // The verdict is the training label the capture pipeline exists for:
  // `worked | partial | wrong`, with `recommended` saying whether the turn
  // concluded something (asked outright) or was a lookup (a quiet thumbs
  // pair), and `has_detail` whether they typed what actually fixed it. Never
  // the text itself.
  VUE_CONSOLE_SESSION_STARTED: 'vue_console_session_started',
  VUE_CONSOLE_TURN_SENT: 'vue_console_turn_sent',
  VUE_CONSOLE_VERDICT_SUBMITTED: 'vue_console_verdict_submitted',
  VUE_CONSOLE_SESSION_CLOSED: 'vue_console_session_closed',
  /**
   * Thumbs up/down on any AI analysis surface (case / property / call). Carries
   * `surface` rather than splitting into one event per card, so adoption of the
   * affordance is one funnel — the first thing worth knowing is whether anyone
   * clicks it at all. Vue chat keeps its own event above for continuity.
   */
  AI_FEEDBACK_SUBMITTED: 'ai_feedback_submitted',
  /**
   * Verdict on one automated-triage play group on the issue sheet. Separate
   * from AI_FEEDBACK_SUBMITTED because it is not the same act: that one rates
   * a model's answer and writes a Langfuse score, this one labels a
   * deterministic rules engine's classification as ground truth in Postgres.
   * Carries `play` and `verdict`, which is what says WHICH rule people
   * distrust — the whole reason the affordance exists.
   */
  TRIAGE_FEEDBACK_SUBMITTED: 'triage_feedback_submitted',

  // Declared-state networking inventory (internal app)
  ISP_CIRCUIT_CREATED: 'isp_circuit_created',
  ISP_CIRCUIT_UPDATED: 'isp_circuit_updated',
  VENDOR_IP_CREATED: 'vendor_ip_created',
  VENDOR_IP_UPDATED: 'vendor_ip_updated',

  // Service Mix Distribution report: a user drilled into a property from the
  // chart (dot click → detail drawer). Params carry the mix combination key
  // + surface only — never the property name.
  SERVICE_MIX_PROPERTY_OPENED: 'service_mix_property_opened',
  // Hub server console — an agent forced a fresh probe of a property's hubs.
  HUB_SERVER_REPROBED: 'hub_server_reprobed',

  // Network health: a user opened the per-property health detail on the org
  // dashboard's "Properties by Health" hot-list (hover tooltip). Params carry
  // the score band only — never the property name.
  NETWORK_HEALTH_DETAIL_VIEWED: 'network_health_detail_viewed',
  // How much of the GPNS estate has migrated off the WiFi Control Panel: a save
  // here permanently detaches the property from the hospitality sync.
  NETWORK_BUILD_SAVED: 'network_build_saved',
  // How many access points still need a human to settle their zone.
  AP_ZONES_SAVED: 'ap_zones_saved',

  // Vendor-invoice workflows. Params carry workflow keys and booleans only.
  AP_TRIAGE_DECIDED: 'ap_triage_decided', // params: { workflow_key, overridden }
  AP_SENT_BACK_TO_TRIAGE: 'ap_sent_back_to_triage', // params: { source: review|approval }
  AP_AUTO_ROUTE_TOGGLED: 'ap_auto_route_toggled', // params: { workflow_key, enabled }

  // Portal Rooms (customer-facing room-grid)
  PORTAL_ROOMS_VIEWED: 'portal_rooms_viewed',
  PORTAL_ROOMS_VIEW_MODE_CHANGED: 'portal_rooms_view_mode_changed',

  // Live View — a user opened the property's in-room TV app through the
  // /liveview reverse proxy (new tab). Params carry surface + server_count
  // only, never property names.
  LIVE_VIEW_OPENED: 'live_view_opened',

  // Customizable dashboards — a user tailored their widget canvas for a
  // surface. Params carry surface_key + widget_id/count + span, never content.
  DASHBOARD_EDIT_OPENED: 'dashboard_edit_opened',
  DASHBOARD_WIDGET_ADDED: 'dashboard_widget_added',
  DASHBOARD_WIDGET_REMOVED: 'dashboard_widget_removed',
  DASHBOARD_LAYOUT_SAVED: 'dashboard_layout_saved',
  DASHBOARD_LAYOUT_RESET: 'dashboard_layout_reset',
  DASHBOARD_PRESET_CHANGED: 'dashboard_preset_changed',

  // Public landing page (base URL)
  // A visitor submitted the waitlist form. Params carry role/property-count
  // selections only — never the name, email, or company (PII).
  WAITLIST_JOINED: 'waitlist_joined',

  // Logistics (warehouse device testing). Params carry
  // station/model/verdict/counts only — never individual serials in bulk
  // contexts.
  LOGISTICS_ASSET_SCANNED: 'logistics_asset_scanned',
  LOGISTICS_TEST_RUN_COMPLETED: 'logistics_test_run_completed',
  LOGISTICS_ASSET_CREATED: 'logistics_asset_created',
  LOGISTICS_ASSETS_IMPORTED: 'logistics_assets_imported',
  LOGISTICS_MODEL_CREATED: 'logistics_model_created',
  LOGISTICS_FAULT_CODE_CREATED: 'logistics_fault_code_created',
  LOGISTICS_TEST_PROFILE_SAVED: 'logistics_test_profile_saved',
  LOGISTICS_CAMERA_SCAN_OPENED: 'logistics_camera_scan_opened',

  // Engagement Management System (house channels / playlists / media
  // library / promotions). Params carry ids/counts/sizes only — never
  // filenames or titles (free text).
  ENGAGEMENT_MEDIA_UPLOADED: 'engagement_media_uploaded',
  ENGAGEMENT_PLAYLIST_CREATED: 'engagement_playlist_created',
  ENGAGEMENT_PLAYLIST_UPDATED: 'engagement_playlist_updated',
  ENGAGEMENT_CHANNEL_ASSIGNED: 'engagement_channel_assigned',
  ENGAGEMENT_PLAYLIST_DEPLOYED: 'engagement_playlist_deployed',
  ENGAGEMENT_PROMO_PLACEMENT_CREATED: 'engagement_promo_placement_created',
  ENGAGEMENT_PROMO_PLACEMENT_UPDATED: 'engagement_promo_placement_updated',
  ENGAGEMENT_PROMOS_DEPLOYED: 'engagement_promos_deployed',
  // Phase 3: org-level shared library + multi-site publish.
  ENGAGEMENT_ORG_PUBLISHED: 'engagement_org_published',
  // Import-from-hub: reconciled a slot's declared playlist to what the hub
  // was already playing live.
  ENGAGEMENT_SLOT_IMPORTED: 'engagement_slot_imported',
  ENGAGEMENT_CAROUSEL_ASSIGNED: 'engagement_carousel_assigned',
  ENGAGEMENT_CAROUSEL_DEPLOYED: 'engagement_carousel_deployed',
  ENGAGEMENT_CAROUSEL_IMPORTED: 'engagement_carousel_imported',
  // Org Playlists tab: moved a site playlist to org scope, and re-deployed
  // an org playlist to every property currently subscribed to it.
  ENGAGEMENT_PLAYLIST_PROMOTED: 'engagement_playlist_promoted',
  ENGAGEMENT_PLAYLIST_SYNCED: 'engagement_playlist_synced',

  // Notification inbox (fired by the pipeline surfaces themselves — feature
  // teams emitting notification events add nothing, plan §7). `opened` = an
  // inbox item marked read via any surface; `clicked` = a deep-link followed.
  NOTIFICATION_OPENED: 'notification_opened',
  NOTIFICATION_CLICKED: 'notification_clicked',

  // Fleet Lens refresh estimate: the budgetary workbook was downloaded.
  REFRESH_ESTIMATE_EXPORTED: 'refresh_estimate_exported',

  // A snapshot report was run from the Reports page and downloaded (nothing
  // stored), or started for email delivery. Params: { report_id, output,
  // delivery: 'download' | 'email' }.
  REPORT_GENERATED: 'report_generated',
  // A schedule was saved for a snapshot report.
  // Params: { report_id, cadence_kind }.
  REPORT_SCHEDULED: 'report_scheduled',
  // Someone was added to or removed from a Reporting > Maintenance email list.
  // Params: { list: 'failures' | 'receive_all', member, self }.
  REPORT_MAINTENANCE_LIST_CHANGED: 'report_maintenance_list_changed',
  // Someone else was subscribed to, or unsubscribed from, a report schedule
  // (subscribe-others). Params: { subscribed }.
  REPORT_SUBSCRIBER_CHANGED: 'report_subscriber_changed',

  // "Get the Atlas app" bottom sheet for mobile-web visitors without the app
  // (non-Safari-iOS + Android only — Safari gets Apple's own Smart App
  // Banner instead). Params carry { audience, platform } on all three.
  MOBILE_APP_BANNER_SHOWN: 'mobile_app_banner_shown',
  MOBILE_APP_BANNER_DISMISSED: 'mobile_app_banner_dismissed',
  MOBILE_APP_BANNER_CTA_TAPPED: 'mobile_app_banner_cta_tapped',

  // Metrics app — the governed metric dictionary + explorer
  METRIC_CERTIFICATION_CHANGED: 'metric_certification_changed',
  METRIC_BACKFILL_STARTED: 'metric_backfill_started',
  METRICS_EXPLORED: 'metrics_explored',
  OKR_DEPARTMENT_CREATED: 'okr_department_created',
  OKR_DEPARTMENT_RENAMED: 'okr_department_renamed',
  OKR_OBJECTIVE_CREATED: 'okr_objective_created',
  OKR_OBJECTIVE_UPDATED: 'okr_objective_updated',
  OKR_OBJECTIVE_ARCHIVED: 'okr_objective_archived',
  OKR_KEY_RESULT_ADDED: 'okr_key_result_added',
  OKR_KEY_RESULT_UPDATED: 'okr_key_result_updated',
  OKR_KEY_RESULT_REMOVED: 'okr_key_result_removed',

  // Knowledge app (the KB inside Atlas). VIEWED fires once per article with
  // { section, depth }; SEARCHED on a committed search and TABLE_FILTERED once
  // an in-page table filter settles, both with { query_length, result_count }
  // — never the query text.
  KNOWLEDGE_ARTICLE_VIEWED: 'knowledge_article_viewed',
  KNOWLEDGE_SEARCHED: 'knowledge_searched',
  KNOWLEDGE_TABLE_FILTERED: 'knowledge_table_filtered',
  // Editing: PUBLISHED with { section, first_publish, has_note }; DOCUMENT_PASTED
  // when a whole drafted page is pasted in, with { field_count } (details it filled).
  KNOWLEDGE_PAGE_PUBLISHED: 'knowledge_page_published',
  KNOWLEDGE_DOCUMENT_PASTED: 'knowledge_document_pasted',
  // Page management (_manage): one event, { action } names the change
  // (page_created, moved, reordered, deleted, restored…).
  KNOWLEDGE_TREE_CHANGED: 'knowledge_tree_changed',
  // Write with Vue: WRITTEN when a draft finishes, with { instruction_length,
  // new_page, passed (it compiles), attempt }; USED when the editor takes it,
  // with { attempts }.
  KNOWLEDGE_VUE_DRAFT_WRITTEN: 'knowledge_vue_draft_written',
  KNOWLEDGE_VUE_DRAFT_USED: 'knowledge_vue_draft_used',
  // An MCP client (Claude Code, claude.ai) asked to act as the person on
  // /oauth/consent, with { accepted, hosted } (hosted: claude.ai, not localhost).
  MCP_CONSENT_ANSWERED: 'mcp_consent_answered',

  // Customer / org overview at-a-glance
  CASES_TILE_OPENED: 'cases_tile_opened',
  OVERVIEW_ISSUE_OPENED: 'overview_issue_opened',

  // OTA Coverage (Internal property page). VIEWED fires once per property
  // load with { coverage_state, receivable_count, studied_count };
  // UNRECEIVABLE_SHOWN when staff reveal the tier-0 / beyond-range rows;
  // STATION_OPENED with { service, tier_rank } when a station's detail opens;
  // ANTENNA_HEIGHT_SAVED with { height_ft, previous_height_ft, was_default }.
  OTA_COVERAGE_VIEWED: 'ota_coverage_viewed',
  OTA_COVERAGE_UNRECEIVABLE_SHOWN: 'ota_coverage_unreceivable_shown',
  OTA_COVERAGE_STATION_OPENED: 'ota_coverage_station_opened',
  OTA_ANTENNA_HEIGHT_SAVED: 'ota_antenna_height_saved',
  // The internal map's Nielsen market layer switched on. { surface }.
  NIELSEN_MARKETS_SHOWN: 'nielsen_markets_shown'
} as const

export type AnalyticsEventName =
  (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents]

// Dual-fire: GA4 (primary, retiring) + PostHog (flags / replay / high-
// cardinality properties). Same event names land in both so dashboards can
// be compared during the transition. Either sink no-ops when disabled.
export function trackEvent(
  name: AnalyticsEventName,
  params?: GtagParams
): void {
  gtag('event', name, params ?? {})
  getPostHog()?.capture(name, params ?? {})
}
